import { action } from "mobx";
import { Enum } from "helpers";
import { initialState, templateInitialState } from "./state";
import lookupStore from "stores/lookupStore";
import { getBrandedTripettoStyles, TripettoTarget } from "helpers/branding/brandingToTripetto";
import { resolveDefaultSaveResponseDocumentType } from "helpers/saveResponseDocumentType";

const applySaveResponseAsPdfToggle = (store, isOnKey, docTypeKey) => action(value => {
  store[isOnKey] = value;
  if (value && !store[docTypeKey]) {
    const formTypeKey = isOnKey.startsWith("template") ? "templateFormType" : "formType";
    const isContactForm = store[formTypeKey] === "contact";
    const categories = isContactForm
      ? lookupStore.contactAttachmentCategories
      : lookupStore.candidateAttachmentCategories;
    const defaults = isContactForm
      ? lookupStore.contactAttachmentCategoriesDefaults
      : lookupStore.candidateAttachmentCategoriesDefaults;
    store[docTypeKey] = resolveDefaultSaveResponseDocumentType(categories, defaults);
  }
  if (!value) {
    store[docTypeKey] = null;
  }
});
// Array setters
const createArraySetter = (store, key) =>
  action(items => {
    // Reset only the specific array property, not affecting other store properties
    if (Array.isArray(items)) {
      store[key] = [...items];
    } else if (items?.items && Array.isArray(items.items)) {
      store[key] = [...items.items];
    } else if (items === null || items === undefined) {
      store[key] = [];
    } else {
      console.warn(`${key} received invalid data:`, items);
      // Keep the existing array if the items are invalid
      if (!Array.isArray(store[key])) {
        store[key] = [];
      }
    }
  });

// Simple setters
const createSimpleSetter = (store, key) =>
  action(value => {
    store[key] = value;
  });

// Object setters
const createObjectSetter = (store, key, defaultValues = {}) =>
  action(data => {
    store[key] = {};
    if (data && typeof data === "object") {
      store[key] = {
        ...defaultValues,
        ...data
      };
    } else {
      console.warn(`${key} received invalid data:`, data);
      store[key] = { ...defaultValues };
    }
  });

export const createMutations = store => ({
  // Simple setters
  setFormDraftName: createSimpleSetter(store, "formDraftName"),
  setCurrentPage: createSimpleSetter(store, "currentPage"),
  setPageSize: createSimpleSetter(store, "pageSize"),
  setFilterSearchText: createSimpleSetter(store, "filterSearchText"),
  setFilterModel: createSimpleSetter(store, "filterModel"),
  setIsLoading: createSimpleSetter(store, "isLoading"),
  setRowData: createSimpleSetter(store, "rowData"),
  setShowFilterModal: createSimpleSetter(store, "showFilterModal"),
  setTotalRow: createSimpleSetter(store, "totalRow"),
  setHasChanges: createSimpleSetter(store, "hasChanges"),
  setActiveFormTab: createSimpleSetter(store, "activeFormTab"),
  setFormDescription: createSimpleSetter(store, "formDescription"),
  setFormCategories: createSimpleSetter(store, "formCategories"),
  setFormFace: createSimpleSetter(store, "formFace"),
  setFormStyles: createSimpleSetter(store, "formStyles"),
  setIsRestoreForm: createSimpleSetter(store, "isRestoreForm"),
  setTripettoBuilder: createSimpleSetter(store, "tripettoBuilder"),
  setIsOpenEditConfirmationPopup: createSimpleSetter(store, "isOpenEditConfirmationPopup"),
  setSuggestions: createArraySetter(store, "suggestions"),
  // Add missing methods mirroring surveyStore
  setIsFormDirty: createSimpleSetter(store, "isFormDirty"),
  setInitialFormData: action(initialData => {
    store.initialFormData = {};
    if (initialData && typeof initialData === "object") {
      store.initialFormData = { ...initialData };
    }
  }),
  setIsLoaded: createSimpleSetter(store, "isLoaded"),
  setFormPublishState: createSimpleSetter(store, "formPublishState"),

  // Alert field setters
  setFormType: createSimpleSetter(store, "formType"),
  setIsSendAlert: createSimpleSetter(store, "isSendAlert"),
  setAlertRecipients: createSimpleSetter(store, "alertRecipients"),
  setAlertUserIds: createSimpleSetter(store, "alertUserIds"),
  setAlertExternalEmails: createSimpleSetter(store, "alertExternalEmails"),
  setAlertSubject: createSimpleSetter(store, "alertSubject"),
  setAlertMessage: createSimpleSetter(store, "alertMessage"),
  setAlertFrequency: createSimpleSetter(store, "alertFrequency"),
  setAlertTime: createSimpleSetter(store, "alertTime"),
  setIsSaveResponseAsPdf: applySaveResponseAsPdfToggle(store, "isSaveResponseAsPdf", "saveResponseDocumentType"),
  setSaveResponseDocumentType: createSimpleSetter(store, "saveResponseDocumentType"),
  setGoogleAnalytics: createSimpleSetter(store, "googleAnalytics"),

  // Automation users setter
  setAutomationUsers: createArraySetter(store, "automationUsers"),
  setAutomationRoiUsers: createArraySetter(store, "automationRoiUsers"),

  // Array setters
  setForms: createArraySetter(store, "forms"),

  // Object setters
  setForm: createObjectSetter(store, "form"),
  setFormData: createObjectSetter(store, "formData"),

  setDefaultState: action(() => {
    Object.keys(initialState).forEach(key => {
      store[key] = initialState[key];
    });
    // Initialize form state to inactive for new forms
    store.formPublishState = "inactive";
  }),


  // Template setters
  setTemplateCurrentPage: createSimpleSetter(store, "templateCurrentPage"),
  setTemplatePageSize: createSimpleSetter(store, "templatePageSize"),
  setTemplateFilterSearchText: createSimpleSetter(store, "templateFilterSearchText"),
  setTemplateFilterModel: createSimpleSetter(store, "templateFilterModel"),
  setTemplateFormDraftName: createSimpleSetter(store, "templateFormDraftName"),
  setTemplateIsLoading: createSimpleSetter(store, "templateIsLoading"),
  setTemplateRowData: createSimpleSetter(store, "templateRowData"),
  setTemplateShowFilterModal: createSimpleSetter(store, "templateShowFilterModal"),
  setTemplateTotalRow: createSimpleSetter(store, "templateTotalRow"),
  setTemplateHasChanges: createSimpleSetter(store, "templateHasChanges"),
  setTemplateActiveFormTab: createSimpleSetter(store, "templateActiveFormTab"),
  setTemplateFormDescription: createSimpleSetter(store, "templateFormDescription"),
  setTemplateFormCategories: createArraySetter(store, "templateFormCategories"),
  setTemplateFormFace: createSimpleSetter(store, "templateFormFace"),
  setTemplateFormStyles: createSimpleSetter(store, "templateFormStyles"),
  setTemplateShowWizardModal: createSimpleSetter(store, "templateShowWizardModal"),
  setTemplateCategoryOptions: createArraySetter(store, "templateCategoryOptions"),

  // Template Alert field setters
  setTemplateFormType: createSimpleSetter(store, "templateFormType"),
  setTemplateIsSendAlert: createSimpleSetter(store, "templateIsSendAlert"),
  setTemplateAlertRecipients: createSimpleSetter(store, "templateAlertRecipients"),
  setTemplateAlertUserIds: createSimpleSetter(store, "templateAlertUserIds"),
  setTemplateAlertExternalEmails: createSimpleSetter(store, "templateAlertExternalEmails"),
  setTemplateAlertSubject: createSimpleSetter(store, "templateAlertSubject"),
  setTemplateAlertMessage: createSimpleSetter(store, "templateAlertMessage"),
  setTemplateAlertFrequency: createSimpleSetter(store, "templateAlertFrequency"),
  setTemplateAlertTime: createSimpleSetter(store, "templateAlertTime"),
  setTemplateIsSaveResponseAsPdf: applySaveResponseAsPdfToggle(store, "templateIsSaveResponseAsPdf", "templateSaveResponseDocumentType"),
  setTemplateSaveResponseDocumentType: createSimpleSetter(store, "templateSaveResponseDocumentType"),
  setTemplateGoogleAnalytics: createSimpleSetter(store, "templateGoogleAnalytics"),

  // Template Automation users setter
  setTemplateAutomationUsers: createArraySetter(store, "templateAutomationUsers"),
  setTemplateAutomationRoiUsers: createArraySetter(store, "templateAutomationRoiUsers"),

  // Array setters
  setTemplateForms: createArraySetter(store, "templateForms"),

  // Object setters
  setTemplateForm: createObjectSetter(store, "templateForm"),
  setTemplateFormData: createObjectSetter(store, "templateFormData"),
  setTemplateDefaultState: action(() => {
    Object.keys(templateInitialState).forEach(key => {
      store[key] = templateInitialState[key];
    });
  }),
  restoreBuilder: action(() => {
    if (store.tripettoBuilder) {
      store.tripettoBuilder.restore();
    }
    store.isRestoreForm = true;
    return Promise.resolve();
  }),
  resetTripettoBuilder: action(() => {
    store.tripettoBuilder = null;
    store.form = {};
    store.templateForm = {};
    store.formData = {};
    store.templateFormData = {};
    store.formCategories = [];
    store.templateFormCategories = [];
    store.formDescription = "";
    store.templateFormDescription = "";
    // New forms start with the company branding when it is saved.
    store.formStyles = getBrandedTripettoStyles(store.formFace, TripettoTarget.Form) || {
      noBranding: true,
    };
    store.isLoaded = false;
    store.templateIsLoaded = false;
    
    // Reset alert fields
    store.formType = "candidate";
    store.isSendAlert = false;
    store.alertRecipients = "";
    store.alertUserIds = "";
    store.alertExternalEmails = "";
    store.alertSubject = "";
    store.alertMessage = "";
    store.alertFrequency = "immediately";
    store.alertTime = null;
    store.isSaveResponseAsPdf = false;
    store.saveResponseDocumentType = null;
    store.googleAnalytics = "";

    // Reset template alert fields
    store.templateFormType = "candidate";
    store.templateIsSendAlert = false;
    store.templateAlertRecipients = "";
    store.templateAlertUserIds = "";
    store.templateAlertExternalEmails = "";
    store.templateAlertSubject = "";
    store.templateAlertMessage = "";
    store.templateAlertFrequency = "immediately";
    store.templateAlertTime = null;
    store.templateIsSaveResponseAsPdf = false;
    store.templateSaveResponseDocumentType = null;
    store.templateGoogleAnalytics = "";

    // Reset automation users
    // store.automationUsers = [];
    // store.templateAutomationUsers = [];
  }),
});